import React from 'react';
import { CheckCircle } from 'lucide-react';
import './PlanCard.css';

export interface PlanCardPlan {
  id: string;
  slug: string;
  name: string;
  tagline?: string | null;
  price_monthly_cents: number;
  price_annual_cents: number;
  max_products: number;
  max_team_members: number;
  features?: unknown;
}

export type BillingCycle = 'monthly' | 'annual';

function formatMoney(cents: number): string {
  if (cents <= 0) return 'Custom';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function parseFeatures(features: unknown): string[] {
  if (Array.isArray(features)) {
    return features.filter((x): x is string => typeof x === 'string');
  }
  return [];
}

export interface PlanCardProps {
  plan: PlanCardPlan;
  billingCycle: BillingCycle;
  featured?: boolean;
  badge?: string;
  ctaLabel: string;
  onCta: () => void;
  disabled?: boolean;
  /** Enterprise / contact sales — hide price line */
  isEnterprise?: boolean;
  /** When false, omit “Up to N products / team members” rows (e.g. marketing-only cards). */
  showPlanLimits?: boolean;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  billingCycle,
  featured,
  badge,
  ctaLabel,
  onCta,
  disabled,
  isEnterprise,
  showPlanLimits = true,
}) => {
  const monthly = plan.price_monthly_cents;
  const annual = plan.price_annual_cents;
  const showPrice = !isEnterprise && monthly > 0;
  const priceLine =
    billingCycle === 'annual' && annual > 0
      ? `${formatMoney(Math.round(annual / 12))}/mo`
      : `${formatMoney(monthly)}/mo`;
  const billed =
    billingCycle === 'annual' && annual > 0 ? `${formatMoney(annual)} billed annually` : null;

  const features = parseFeatures(plan.features);

  return (
    <div className={`plan-card ${featured ? 'plan-card--featured' : ''}`}>
      {badge ? <div className="plan-card__badge">{badge}</div> : null}
      <h3 className="plan-card__name">{plan.name}</h3>
      {plan.tagline ? <p className="plan-card__tagline">{plan.tagline}</p> : null}
      {showPrice ? (
        <div className="plan-card__price-block">
          <span className="plan-card__price">{priceLine}</span>
          {billed ? <span className="plan-card__billed">{billed}</span> : null}
        </div>
      ) : (
        <div className="plan-card__price-block">
          <span className="plan-card__price plan-card__price--custom">Let&apos;s talk</span>
        </div>
      )}
      <ul className="plan-card__features">
        {showPlanLimits ? (
          <>
            <li>
              <CheckCircle className="plan-card__check" size={18} aria-hidden />
              Up to {plan.max_products} products
            </li>
            <li>
              <CheckCircle className="plan-card__check" size={18} aria-hidden />
              Up to {plan.max_team_members} team members
            </li>
          </>
        ) : null}
        {features.map((f) => (
          <li key={f}>
            <CheckCircle className="plan-card__check" size={18} aria-hidden />
            {f}
          </li>
        ))}
      </ul>
      <button type="button" className="plan-card__cta" onClick={onCta} disabled={disabled}>
        {ctaLabel}
      </button>
    </div>
  );
};
